import axios from 'axios';
import $ from 'jquery';

import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import CreditAccount from 'app/src/data/models/CreditAccount';
import CreditAccountCollection from 'app/src/data/models/CreditAccountCollection';
import PaymentType from 'app/src/data/models/PaymentType';
import Filter from 'app/src/data/models/utils/Filter';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Modules from 'shared_modules/Modules';

const _EAN_GIFT_CARD_NUMBER_PATTERN = /^(4[0-9]{10})[0-9]{0,1}$/;
const _UPC_GIFT_CARD_NUMBER_PATTERN = /^(23[0-9]{10})[0-9]{0,1}$/;
const _GIFT_CARD_NUMBER_PATTERN = /^[0-9]{12,}$/;
export const OMNI_GC_CODE_PREFIX = 'OMNI_GC';
const OMNI_GC_MAX_CODE_LENGTH = 36;
const OMNI_GC_MIN_CODE_LENGTH = 8;
export const FIND_GIFT_CARDS_BY_CODE_URL = '/admin/gift-card/find-by-code/';

export default class GiftCardManager {
  /**
   * Checks to see if Gift Cards are enabled.
   *
   * @returns {boolean}   True when gift card payments are enabled
   */
  static paymentsEnabled(giftCardModule) {
    let ret = false;

    if (giftCardModule) {
      ret = Format.parseBoolean(giftCardModule.enabled, false);
    }

    return ret;
  }

  /**
   * @return {boolean}
   */
  static omniGiftCardModuleEnabled() {
    return Modules.hasModule('omni.giftcards');
  }

  /**
   * Fetch the details of the gift card specified.  The deferred is resolved with a CreditAccount
   * model if the gift card number specified is valid and matches to a known gift card.
   *
   * @param giftCardNumber {string}   The number of the gift card to fetch
   * @returns {jQuery.Promise}    If successful, resolves with a CreditAccount model
   *                              for the gift card; otherwise, is rejected.
   */
  static fetchGiftCardDetails(giftCardNumber) {
    let deferred = $.Deferred();

    if (GiftCardManager.isValidCardNumber(giftCardNumber)) {
      // gift cards are treated like specialized credit accounts by the API
      let creditAccounts = new CreditAccountCollection();
      let giftCardFilter = new Filter()
        .exactly('giftCard', true)
        .exactly('code', giftCardNumber)
        // The endpoint will not returned disabled (archived) records unless we set the archived
        // search parameter to true.  It's a bit of a misnomer because for this endpoint it
        // actually retrieves both archived and non-archived records when set to true.
        .exactly('archived', true);

      creditAccounts
        .fetch(giftCardFilter)
        .done(() => {
          if (creditAccounts.length) {
            deferred.resolve(creditAccounts.at(0));
          } else if (
            giftCardNumber !== GiftCardManager._cardNumberWithoutChecksum(giftCardNumber)
          ) {
            // let's try again but without the checksum
            creditAccounts = new CreditAccountCollection();
            giftCardFilter = new Filter()
              .exactly('giftCard', true)
              .exactly('code', GiftCardManager._cardNumberWithoutChecksum(giftCardNumber))
              .exactly('archived', true);

            creditAccounts
              .fetch(giftCardFilter)
              .done(() => {
                if (creditAccounts.length) {
                  deferred.resolve(creditAccounts.at(0));
                } else {
                  deferred.reject();
                }
              })
              .fail(() => {
                deferred.reject();
              });
          } else {
            deferred.reject();
          }
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Creates a gift card using the API.  No amount will be set on the gift card so this is more
   * like activation or registration of the gift card with the API.
   *
   * @param {string} giftCardNumber   The number of the gift card to create
   * @param {number|null} customerID  The (optional) customer ID associated with the gift card
   * @returns {*}
   */
  static createGiftCard(giftCardNumber, customerID = null) {
    let deferred = $.Deferred();

    if (GiftCardManager.isValidCardNumber(giftCardNumber)) {
      const date = new Date();
      let code = giftCardNumber;
      // We need to add OMNI_GC prefix for credit accounts if the merchant has omni gift card module.
      if (Modules.hasModule('omni.giftcards')) {
        code = `${OMNI_GC_CODE_PREFIX}${code}`;
      }
      let giftCard = new CreditAccount({
        name: `Gift Card ${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
        code,
      });

      // the API is super restrictive for this endpoint; make sure we clear any defaults that
      // it will reject
      giftCard.unset('archived');
      giftCard.unset('description');
      giftCard.unset('giftCard');
      giftCard.unset('balance');
      giftCard.unset('creditLimit');

      if (customerID) {
        giftCard.set('customerID', customerID);
      }

      giftCard
        .save()
        .done(() => {
          deferred.resolve(giftCard);
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Determines if the specified card number is a valid card number
   *
   * @param giftCardNumber {string}   The number of the gift card to validate
   * @returns {*}
   */
  static isValidCardNumber(giftCardNumber) {
    // The Gift Card service accepts any kind of number
    if (Modules.hasModule('omni.giftcards')) {
      return (
        giftCardNumber?.length >= OMNI_GC_MIN_CODE_LENGTH &&
        giftCardNumber?.length <= OMNI_GC_MAX_CODE_LENGTH
      );
    }

    return (
      GiftCardManager._isUPCGiftCardNumber(giftCardNumber) ||
      GiftCardManager._isEANGiftCardNumber(giftCardNumber) ||
      GiftCardManager._isGiftCardNumber(giftCardNumber)
    );
  }

  /**
   * Grabs the minimum Gift Card refund, if any
   *
   * @param {SaleManager} saleManager
   * @returns {*}
   */
  static getMinimumRefund(saleManager) {
    let minimumRefund = new Decimal(0);

    if (saleManager.isRefundSale()) {
      let giftCardPaymentType = saleManager.getRefundablePaymentTypeByID(
        GiftCardManager.getGiftCardPaymentTypeID()
      );
      if (giftCardPaymentType) {
        minimumRefund = giftCardPaymentType.minimumRefund;
      }
    }

    return minimumRefund;
  }

  /**
   * Strips out any checksum from the specified gift card number.  The checksum would be present in
   * data that was obtained directly from a scanning device.  For data that was entered manually,
   * it likely would not.
   *
   * @param giftCardNumber {string}   The number of the gift card
   * @returns {string|null}   If the string is a valid gift card number then it returns the card number
   *                          without any checksum
   * @private
   */
  static _cardNumberWithoutChecksum(giftCardNumber) {
    let cardNumberWithoutChecksum = null;

    if (GiftCardManager._isUPCGiftCardNumber(giftCardNumber)) {
      // the pattern is structured to group all the digits except for the last (the checksum)
      // in the first group so we just need to match and grab it
      cardNumberWithoutChecksum = giftCardNumber.match(_UPC_GIFT_CARD_NUMBER_PATTERN)[1];
    } else if (GiftCardManager._isEANGiftCardNumber(giftCardNumber)) {
      // the pattern is structured to group all the digits except for the last (the checksum)
      // in the first group so we just need to match and grab it
      cardNumberWithoutChecksum = giftCardNumber.match(_EAN_GIFT_CARD_NUMBER_PATTERN)[1];
    } else if (GiftCardManager._isGiftCardNumber(giftCardNumber)) {
      // any 12 digit number may be a valid gift card and there won't be any checksum
      cardNumberWithoutChecksum = giftCardNumber;
    }
    return cardNumberWithoutChecksum;
  }

  /**
   * Determines if a gift card number is a valid UPC gift card number
   *
   * @param giftCardNumber {string}   The number of the gift card
   * @returns {boolean}               True if the gift card number is a UPC gift card number
   * @private
   */
  static _isUPCGiftCardNumber(giftCardNumber) {
    let isUPCGiftCardNumber = false;

    if (giftCardNumber && giftCardNumber.match) {
      isUPCGiftCardNumber = giftCardNumber.match(_UPC_GIFT_CARD_NUMBER_PATTERN) !== null;
    }

    return isUPCGiftCardNumber;
  }

  /**
   * Determines if a gift card number is a valid EAN gift card number
   *
   * @param giftCardNumber {string}   The number of the gift card
   * @returns {boolean}               True if the gift card number is a EAN gift card number
   * @private
   */
  static _isEANGiftCardNumber(giftCardNumber) {
    let isEANGiftCardNumber = false;

    if (giftCardNumber && giftCardNumber.match) {
      isEANGiftCardNumber = giftCardNumber.match(_EAN_GIFT_CARD_NUMBER_PATTERN) !== null;
    }

    return isEANGiftCardNumber;
  }

  /**
   * Determines if a gift card number is a valid gift card number
   *
   * @param giftCardNumber {string}   The number of the gift card
   * @returns {boolean}               True if the gift card number is a gift card number
   * @private
   */
  static _isGiftCardNumber(giftCardNumber) {
    let isGiftCardNumber = false;

    if (giftCardNumber && giftCardNumber.match) {
      isGiftCardNumber = giftCardNumber.match(_GIFT_CARD_NUMBER_PATTERN) !== null;
    }

    return isGiftCardNumber;
  }

  /**
   * Gets the ID of the Gift Card Payment Type
   *
   * @returns {number}
   */
  static getGiftCardPaymentTypeID() {
    return PaymentTypeManager.getPaymentIdForType(PaymentType.Types.GIFT_CARD);
  }

  /**
   * @param giftCardNumber {string}
   * @param includeAwaitingActivation {boolean}
   * @returns {Promise<Array>}
   */
  static async findGiftCardsByCode(giftCardNumber, includeAwaitingActivation = false) {
    const params = {
      include_awaiting_activation: includeAwaitingActivation,
    };
    const url = FIND_GIFT_CARDS_BY_CODE_URL + encodeURIComponent(giftCardNumber);
    const response = await axios.get(url, {
      withCredentials: true,
      params,
      validateStatus: (status) => status === 200,
    });
    return response?.data?.data?.giftCards;
  }

  /**
   * @param CustomerId {Array}
   * @return {Promise<Array>}
   */
  static async getCustomers(CustomerId) {
    if (!CustomerId.length) return [];
    const url = `${URLHelper.getAccountURL('/Customer.json?customerID=IN,')}${JSON.stringify(
      CustomerId
    )}`;
    const response = await axios.get(url);
    const attributes = response?.data['@attributes'];
    const count = parseInt(attributes.count, 10);
    if (!count) return [];
    const customer = response?.data?.Customer;
    if (count === 1) return [customer];
    return customer;
  }
}
